import { useMemo } from 'react';
import { Building2 } from 'lucide-react';
import { SPECIALTY_LABEL } from '@hc/shared';
import { PanelShell } from '../panel-shell.js';
import { PanelEmptyState } from '../panel-empty-state.js';
import { useGlobalCurrency, type PanelId, type TabId } from '../store.js';
import { formatEurInCurrency } from '../../currency.js';
import { useGroupKey, useGroupLink } from '../group-link.js';
import { OWNERSHIP_CLASS_LABEL } from './ownership-network.js';

// Group Overview — the standalone economic-group record (the Record/Company
// Overview pattern, but for GROUPS). Channel-linked: an Ownership Network panel
// on the same channel broadcasts the focused group (group-link); this panel
// renders the group-altitude detail — identity, backing, and the holding-company
// structure. Individual operating entities are NOT enumerated here: clicking an
// operator node in the graph broadcasts its entity id on the same channel, so a
// linked Record / Company Overview panel is where the single-company detail lives.

export function GroupOverviewPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const key = useGroupKey(tabId, panelId);
  const active = useGroupLink((s) => s.byKey[key] ?? null);
  const ccy = useGlobalCurrency();
  const g = active?.node ?? null;

  const holdcos = useMemo(() => g?.holdcos.filter((h) => h.opcos > 0) ?? [], [g]);

  if (!g) {
    return (
      <PanelShell tabId={tabId} panelId={panelId}>
        <PanelEmptyState icon={<Building2 size={22} />} title="No group selected" fill>
          Link this panel to the same channel as an Ownership Network panel, then click a group in
          the graph — its full profile renders here.
        </PanelEmptyState>
      </PanelShell>
    );
  }

  const indep = g.ownershipClass === 'independent';
  const fmt = (n: number) => formatEurInCurrency(n, ccy);

  return (
    <PanelShell tabId={tabId} panelId={panelId} padding="0">
      <div style={{ padding: '14px 16px 20px', overflowY: 'auto', height: '100%' }}>
        {/* kicker + identity */}
        <div
          style={{
            font: '600 9.5px var(--font-ui, Inter, sans-serif)',
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            color: 'var(--text-tertiary)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {indep ? 'Independent group' : 'Parent group'}
          <span
            style={{
              borderRadius: 5,
              padding: '2px 7px',
              font: '600 9.5px var(--font-ui, Inter, sans-serif)',
              letterSpacing: 0.3,
              textTransform: 'uppercase',
              background: indep ? '#f0e8db' : '#eadff2',
              color: indep ? '#6e5f49' : '#5b3a80',
            }}
          >
            {indep ? 'Independent' : 'Platform'}
          </span>
        </div>
        <h2
          style={{ font: '700 17px/1.25 var(--font-display, Georgia, serif)', margin: '5px 0 2px' }}
        >
          {g.label}
        </h2>
        <div style={{ color: 'var(--text-secondary)', fontSize: 11.5, marginBottom: 10 }}>
          {g.backer ?? 'No resolved backer'} ·{' '}
          {OWNERSHIP_CLASS_LABEL[g.ownershipClass] ?? g.ownershipClass}
          {g.confidence ? ` · ${g.confidence} confidence` : ''}
        </div>

        {/* facts */}
        <div style={{ borderTop: '1px solid var(--hairline)', marginTop: 4 }}>
          <Fact k="Sub-segments" v={g.subsegments.join(', ') || '—'} />
          {g.specialties.length > 0 && (
            <Fact
              k="Specialties"
              v={g.specialties.map((s) => SPECIALTY_LABEL[s] ?? s).join(', ')}
            />
          )}
          <Fact k="Revenue (in scope)" v={fmt(g.revenueEur)} />
          <Fact k="Operators" v={String(g.opcos)} />
          <Fact k="Holding companies" v={String(holdcos.length)} />
          {g.regions.length > 0 && <Fact k="Regions" v={g.regions.slice(0, 5).join(', ')} />}
          {g.sponsorStatus && <Fact k="Sponsor status" v={g.sponsorStatus.replace(/_/g, ' ')} />}
        </div>

        {/* holdcos */}
        {holdcos.length > 1 && (
          <Section title="Holding companies">
            {holdcos.map((h, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  padding: '5px 6px',
                  fontSize: 11.5,
                  color: 'var(--text-secondary)',
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: '#b8a98f',
                    flex: 'none',
                  }}
                />
                <span
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {h.name ?? 'Directly held'}
                </span>
                <span
                  style={{
                    marginLeft: 'auto',
                    color: 'var(--text-tertiary)',
                    fontSize: 10,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h.country} · {h.opcos} · {fmt(h.revenueEur)}
                </span>
              </div>
            ))}
          </Section>
        )}

        {/* Individual operators live in the linked Record / Company Overview
            panel — click an operator node in the Ownership Network graph. */}
        <div
          style={{
            marginTop: 14,
            padding: '9px 10px',
            border: '1px solid var(--hairline)',
            borderRadius: 8,
            background: 'var(--hover-subtle)',
            fontSize: 10.5,
            color: 'var(--text-tertiary)',
            lineHeight: 1.5,
          }}
        >
          {g.opcos} operating {g.opcos === 1 ? 'entity' : 'entities'} in this group. Click an
          operator node in the Ownership Network graph to open it in a linked Record panel.
        </div>
      </div>
    </PanelShell>
  );
}

function Fact({ k, v }: { k: string; v: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 10,
        padding: '6.5px 0',
        borderBottom: '1px solid var(--hairline)',
        fontSize: 11.5,
      }}
    >
      <span style={{ color: 'var(--text-tertiary)', flex: 'none' }}>{k}</span>
      <span style={{ textAlign: 'right', color: 'var(--text-primary)', fontWeight: 500 }}>{v}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 14 }}>
      <h4
        style={{
          margin: '0 0 4px',
          font: '600 9.5px var(--font-ui, Inter, sans-serif)',
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          color: 'var(--text-tertiary)',
        }}
      >
        {title}
      </h4>
      {children}
    </div>
  );
}
